import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { BsChatLeftTextFill, BsQuestionCircleFill, BsFillBarChartFill, BsFillInboxFill, BsFillMegaphoneFill, BsFillGearFill, BsShieldLockFill } from 'react-icons/bs'
import { FaUsers, FaDatabase, FaListUl, FaQuestionCircle, FaUsersCog } from 'react-icons/fa'
import home_logo from 'assets/images/botsfactory_logo_white.svg'
import { useTranslation } from 'react-i18next';
import AuthService from 'api/services/AuthService';
import PropTypes from 'prop-types';


function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  data
}) {
  const { t } = useTranslation();

  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef(null);
  const sidebar = useRef(null);

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded, setSidebarExpanded] = useState(storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true');


  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [sidebarOpen, setSidebarOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [sidebarExpanded, sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded);
    if (sidebarExpanded) {
      document.querySelector('body').classList.add('sidebar-expanded');
    } else {
      document.querySelector('body').classList.remove('sidebar-expanded');
    }
  }, [sidebarExpanded]);

  return (
    <div className='side_bar'>
      {/* Sidebar backdrop (mobile only) */}
      <div className={`fixed inset-0 bg-gray-900 bg-opacity-30 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} aria-hidden="true"></div>

      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`bg-gray-800 flex flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 transform h-screen overflow-y-scroll lg:overflow-y-auto no-scrollbar w-64 lg:w-20 lg:sidebar-expanded:!w-64 2xl:!w-64 shrink-0  p-4 transition-all duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-64'}`}
      >

        {/* Sidebar header */}
        <div className="flex justify-between mb-10 pr-3 sm:px-2">
          {/* Close button */}
          <button
            ref={trigger}
            className="lg:hidden text-slate-500 hover:text-slate-400"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>

          </button>
          {/* Logo */}
          <NavLink to="/" className="block">
            <div className='flex items-center'>
              <img src={home_logo} alt='moncf' className='w-8 text-blue' />
              <hr className="w-px h-6 bg-gray-200 mx-3 lg:hidden lg:sidebar-expanded:block 2xl:block" />
              <span className='text-white font-semibold mx-2 lg:hidden lg:sidebar-expanded:block 2xl:block'>{t('project_name')} </span>
              {/* <span className='text-white  lg:hidden lg:sidebar-expanded:block xl:block text-xs'>platform</span> */}
            </div>
          </NavLink>
        </div>




        {/* Links */}
        <div className="space-y-8">
          {/* Slider group , Main */}
          <div>
            <h3 className="text-xs uppercase text-slate-500 font-semibold pl-3">
              <span className="hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center w-6" aria-hidden="true">•••</span>
            </h3>

            <ul className="mt-3">
              < DisplayRouteMenu sidebarGroup={data} pathname={pathname} sidebarExpanded />
            </ul>
          </div>

        </div>

        {/* Expand / collapse button */}
        <div className="pt-3 hidden lg:inline-flex 2xl:hidden justify- mt-auto">
          <div className="px-3 py-2">
            <button onClick={() => setSidebarExpanded(!sidebarExpanded)}>
              <span className="sr-only">Expand / collapse sidebar</span>
              <svg className="w-6 h-6 fill-current sidebar-expanded:rotate-180" viewBox="0 0 24 24">
                <path className="text-slate-400" d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z" />
                <path className="text-slate-600" d="M3 23H1V1h2z" />
              </svg>
            </button>
          </div>
        </div>

        <div className={` hidden 2xl:hidden ${sidebarExpanded && 'pt-3  lg:inline-flex  justify- mt-auto '}  `}>
          <PoweredBy />
        </div>

      </div>
    </div>
  );
}

Sidebar.propTypes = {
  sidebarOpen: PropTypes.bool.isRequired,
  setSidebarOpen: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
};

export default Sidebar;


function DisplayRouteMenu({ sidebarGroup, pathname, sidebarExpanded, setSidebarExpanded }) {
  const { t } = useTranslation();

  return (
    <ul>
      {sidebarGroup.map((route, i) => {
        if (route.routes) {
          // Group entry: show if the user has access to at least one child
          const accessibleChildren = route.routes.filter(r => AuthService.hasPermission(r.permission));
          if (accessibleChildren.length === 0) return null;

          const isGroupActive = route.routes.some(r => pathname.startsWith(r.to || r.path));
          return (
            <React.Fragment key={"sidebaritem-" + i}>
              <div className='mt-4 mb-2'>
                <hr className="hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center w-3/4 py-2 mx-auto" aria-hidden="true" />
                <span className={`lg:hidden lg:sidebar-expanded:block 2xl:block text-xs capitalize font-semibold ${isGroupActive ? 'text-slate-300' : 'text-slate-500'}`}>
                  {t(`side_bar.${route._key}`)}
                </span>
              </div>
              <DisplayRouteMenu sidebarGroup={accessibleChildren} pathname={pathname} />
            </React.Fragment>
          );
        }

        // Leaf entry: show if the user has the required permission
        if (!AuthService.hasPermission(route.permission)) return null;

        const to = route.to || route.path;
        const isActive = pathname.startsWith(to);
        return (
          <React.Fragment key={route._key}>
            <li className={`px-3 py-2 rounded-sm mb-0.5 last:mb-0 ${isActive && 'bg-slate-900'}`}>
              <NavLink to={to} className={`block text-slate-200 hover:text-white truncate transition duration-150 ${isActive && 'hover:text-slate-200'}`}>
                <div className="flex items-center">
                  <div className={`shrink-0 fill-current text-white ${isActive && 'text-blue-500'}`}>
                    {getIcon(route._key)}
                  </div>
                  <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">{t(`side_bar.${route._key}`)}</span>
                </div>
              </NavLink>
            </li>
          </React.Fragment>
        );
      })}
    </ul>
  );
}

const getIcon = (key) => {
  let iconList = {
    'analytics': < BsFillBarChartFill />,
    'inbox': <BsFillInboxFill />,
    'data_training': <FaDatabase />,
    'settings': <BsFillGearFill />,
    'customers': <FaUsers />,
    'campaigns': <BsFillMegaphoneFill />,
    'help': <BsQuestionCircleFill />,
    "main_menu": <FaListUl />,
    "faqs": <FaQuestionCircle />,
    'announcements': <BsChatLeftTextFill />,
    'roles': <BsShieldLockFill />,
    'team_users': <FaUsersCog />,
    'default': <div className='w-5 h-5' />
  };
  return (iconList[key] || iconList['default']);
}

function PoweredBy() {
  return (
    <div className='text-sm text-slate-300  px-2'>
      <span className='text-xs'>Powered by</span> <span className='text-blue-500 font-bold'> BotsFactory</span>
    </div>
  )
}

DisplayRouteMenu.propTypes = {
  sidebarGroup: PropTypes.array.isRequired,
  pathname: PropTypes.string.isRequired,
  sidebarExpanded: PropTypes.bool,
  setSidebarExpanded: PropTypes.func,
};
